<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="crm-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("DEAL_STATUSES.TITLE") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('deal-statuses-create')"
          class="mr-4 success"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="deal-statuses-table"
              height="500"
              name="deal-statuses-table"
              :headers="headers"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod,
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in headers"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <span
                  v-if="header.value === 'options'"
                  :key="index"
                  class="d-flex align-center justify-center flex-nowrap"
                >
                  <v-btn
                    v-if="hasPermissions('deal-statuses-edit') && !item.item.is_system"
                    icon
                    color="primary"
                    @click="handleEditClick(item.item.id)"
                  >
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="hasPermissions('deal-statuses-delete') && !item.item.is_system"
                    icon
                    color="red"
                    @click="handleDeleteClick(item.item.id)"
                  >
                    <v-icon dark>mdi-trash-can</v-icon>
                  </v-btn>
                </span>
                <template v-else-if="header.value === 'is_system'">
                  <v-chip
                    :key="index"
                    x-small
                    :color="item.value ? 'primary' : 'grey'"
                    dark
                  >
                    {{
                      item.value
                        ? $t("DEAL_STATUSES.SYSTEM")
                        : $t("DEAL_STATUSES.CUSTOM")
                    }}
                  </v-chip>
                </template>
                <template v-else>
                  {{ item.value || "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <Confirm
      v-model="deleteConfirm"
      :content="$t('GENERAL.DELETE_CONFIRM')"
      @confirm="deleteDealStatus"
      @decline="deleteConfirm = false"
    />
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import Confirm from "@/views/partials/dialogs/Confirm.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";
import {
  GET_DEAL_STATUSES,
  DELETE_DEAL_STATUS,
} from "@/store/dealStatuses.module";

export default {
  name: "ListDealStatuses",
  components: {
    Table,
    Confirm,
  },
  data() {
    return {
      tableName: "deal-statuses-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_DEAL_STATUSES,
      deleteConfirm: false,
      dealStatusForDelete: null,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "32%",
        },
        {
          text: this.$t("DEAL_STATUSES.SLUG"),
          value: "slug",
          align: "center",
          width: "25%",
        },
        {
          text: this.$t("DEAL_STATUSES.TYPE"),
          value: "is_system",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "20%",
        },
      ],
    };
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    this.userStatePrepared = true;
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    handleCreateClick() {
      this.$router.push({ name: "deal-statuses-create" });
    },
    handleEditClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "deal-statuses-edit", params: { id } });
    },
    handleDeleteClick(id) {
      this.dealStatusForDelete = id;
      this.deleteConfirm = true;
    },
    deleteDealStatus() {
      this.isProcessing = true;

      this.$store
        .dispatch(DELETE_DEAL_STATUS, this.dealStatusForDelete)
        .then(() => {
          this.$refs["deal-statuses-table"].fetchServerData();
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.deleteConfirm = false;
        });
    },
  },
};
</script>
